<template>
  <div
    class="tw:flex tw:h-[105px] tw:flex-row tw:rounded-sm tw:border tw:border-solid tw:border-gray-200 tw:p-4"
    :class="{ 'tw:border-orange-300 tw:bg-orange-100': expiredIntegration }"
    :data-testid="`social-integration-row-${integrationId}`"
  >
    <div class="tw:flex tw:w-full tw:flex-row tw:justify-between">
      <div class="tw:flex-col-2 tw:flex">
        <div
          v-if="showCheckbox"
          class="tw:flex tw:h-full tw:flex-col"
        >
          <Checkbox
            class="tw:text-2xl tw:leading-none tw:text-green-400"
            dense
            :disabled="props.isDisabled"
            :model-value="isChecked"
            new-design
            @update:model-value="checkboxClicked"
          >
            <StreemTooltip
              v-if="props.isDisabled"
              anchor="bottom right"
              self="center left"
            >
              {{ $t("social_integration_row.disabled_text") }}
            </StreemTooltip>
          </Checkbox>
        </div>
        <div class="tw:flex tw:h-full tw:flex-col">
          <SocialProfileIcon
            class="tw:ml-5 tw:mr-4"
            data-testid="integration-platform-icon"
            :platform-icon="integration.platformIcon()"
            :profile-url="integration.metadata?.profilePhotoUrl"
          />
        </div>
        <div
          class="tw:flex tw:h-full tw:flex-col tw:font-bold tw:text-denim-900"
          :data-testid="`integration-${integrationId}`"
        >
          {{ integration.label }}<br />
          <span
            v-if="integration.updatedAt"
            class="tw:pt-2 tw:font-normal tw:text-denim-700"
          >
            <slot name="prepend-subtext" />
            <time
              data-testid="token-refreshed-at"
              :datetime="tokenRefreshedAtIso"
              >{{
                $t("social_integration_row.last_updated", {
                  timeAgo: timeAgo(integration.tokenRefreshedAt),
                })
              }}</time
            >
          </span>
          <span class="tw:pt-2 tw:font-normal tw:capitalize tw:text-denim-700">
            <BaseIcon
              v-if="expiredIntegration"
              class="tw:text-orange-500"
              icon="exclamation-circle"
            />
            <slot name="prepend-subtext" />
            <template v-if="props.showAsExpired">
              <time
                v-if="expireLabel.datetime"
                data-testid="expires-at"
                :datetime="expireLabel.datetime"
                >{{ expireLabel.label }}
              </time>
              <template v-else>{{ expireLabel.label }}</template>
            </template>
          </span>
        </div>
      </div>
      <div class="tw:flex-col-2 tw:flex">
        <div class="tw:flex tw:h-full tw:flex-col">
          <SocialEngageAccountTeams
            data-testid="teams"
            :disabled="isReadOnly"
            :integration="integration"
            :organisation-teams="organisationTeams"
            @update:integration="emit('update-integration-team', integration)"
          />
          <div class="tw:flex tw:justify-between tw:gap-2">
            <template v-if="showSocialProduct">
              <Checkbox
                v-if="canCheckSocialEngage"
                v-model="socialProduct.isSocialEngage"
                :disabled="isReadOnly"
                :label="getLocaleText('social_integration_row.engage')"
                new-design
              />
              <Checkbox
                v-if="canCheckSocialListen"
                v-model="socialProduct.isSocialListen"
                :disabled="isReadOnly"
                :label="getLocaleText('social_integration_row.listen')"
                new-design
              />
            </template>
            <template v-else>
              <Checkbox
                class="tw:ml-auto!"
                disabled
                :label="getLocaleText('social_integration_row.engage')"
                model-value
                new-design
              />
            </template>
          </div>
        </div>
        <div
          v-if="integration.id && showActionSheet"
          class="tw:flex tw:h-full tw:flex-col"
        >
          <SocialIntegrationRowActionSheet
            :disable="isReadOnly"
            @remove-integration="emit('remove-integration', integration)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SocialEngageAccountTeams from "desktop/src/components/SocialEngageSettings/SocialEngageAccountTeams.vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { socialFeatures } from "shared/boot/authorization";
import { getLocaleText } from "shared/boot/i18n";
import { BaseIcon, StreemTooltip } from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import SocialIntegrationRowActionSheet from "shared/components/integrations/SocialIntegrationRowActionSheet.vue";
import SocialProfileIcon from "shared/components/SocialProfileIcon.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import { useTransientAlertContext } from "shared/composables/useTransientAlert";
import { type SocialIntegration } from "shared/constants/social";
import { formatIntlDate, timeAgo } from "shared/helpers/date";
import {
  isSocialPlatformTokenValid,
  socialPlatformTokenProjectedExpiry,
} from "shared/helpers/social";
import { type OrganisationTeam } from "shared/resources";
import features from "shared/services/features";
import useGlobalStore from "shared/stores/global";
import { useSocialIntegrationsStore } from "shared/stores/socialIntegrations";
import { useUserStore } from "shared/stores/user";
import type { OrganisationTeam as UserOrganisationTeamOption } from "shared/stores/user/types";
import { type Nullable } from "shared/types";

const LOCALE = "social_integration_row";

const { showSuccess } = useTransientAlertContext();

interface SocialIntegrationCardProps {
  integration: SocialIntegration;
  isChecked?: boolean;
  isDisabled?: boolean;
  showActionSheet?: boolean;
  showCheckbox?: boolean;
  showAsExpired?: boolean;
}

interface ExpireLabel {
  label: string;
  datetime: Nullable<string>;
}
interface SocialProduct {
  isSocialEngage: boolean;
  isSocialListen: boolean;
}

const { isCisionOne } = storeToRefs(useGlobalStore());
const socialIntegrationsStore = useSocialIntegrationsStore();
const { teamIntegrationsRecord } = storeToRefs(socialIntegrationsStore);
const userStore = useUserStore();
const { organisation } = storeToRefs(userStore);

const { organisationTeamOptions } = useTeamPicker();
const props = defineProps<SocialIntegrationCardProps>();

const emit = defineEmits<{
  "remove-integration": [SocialIntegration];
  "update-integration-team": [SocialIntegration];
  "checkbox-clicked": [SocialIntegration];
}>();

const socialProduct = ref<SocialProduct>({
  isSocialEngage: Boolean(props.integration.isSocialEngage),
  isSocialListen: Boolean(props.integration.isSocialListen),
});

const showSocialProduct = computed(
  () =>
    isCisionOne.value &&
    props.integration.socialPlatform === "InstagramSocialIntegration" &&
    (canCheckSocialEngage.value || canCheckSocialListen.value)
);

const canCheckSocialEngage = computed<boolean>(() =>
  socialFeatures.canViewSocialEngage()
);

const canCheckSocialListen = computed<boolean>(() =>
  socialFeatures.canViewSocialListen()
);

const expireLabel = computed<ExpireLabel>(() => {
  let willExpireAt = socialPlatformTokenProjectedExpiry(props.integration);

  if (willExpireAt === Infinity) {
    return { label: getLocaleText(`${LOCALE}.never_expires`), datetime: null };
  }

  const localeKey = isSocialPlatformTokenValid(props.integration)
    ? `${LOCALE}.expires_at`
    : `${LOCALE}.expired_at`;

  willExpireAt =
    willExpireAt instanceof Date ? willExpireAt : new Date(willExpireAt);

  return {
    label: getLocaleText(localeKey, { timeAgo: formatDate(willExpireAt) }),
    datetime: willExpireAt.toISOString(),
  };
});

const tokenRefreshedAtIso = computed<string>(() =>
  new Date(props.integration.tokenRefreshedAt).toISOString()
);

const expiredIntegration = computed<boolean>(
  () => !isSocialPlatformTokenValid(props.integration) && props.showAsExpired
);

const integrationId = computed<string>(
  () => props.integration.id || props.integration.remoteId
);

const organisationTeams = computed<
  UserOrganisationTeamOption[] | OrganisationTeam[]
>(() => {
  if (features.has("has_team_workspaces")) {
    return organisationTeamOptions.value;
  }

  return organisation.value.organisation_teams || [];
});

const isReadOnly = computed<boolean>(() => {
  if (!props.integration.id) return false;

  return !teamIntegrationsRecord.value[props.integration.id];
});

function checkboxClicked(): void {
  emit("checkbox-clicked", props.integration);
}

function formatDate(date: Date): string {
  return formatIntlDate(date, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

watch(
  socialProduct,
  async () => {
    const record = teamIntegrationsRecord.value[integrationId.value];

    if (record) {
      Object.assign(record, socialProduct.value);
      await record.save();

      showSuccess(
        getLocaleText("social_integration_row.social_product_updated"),
        1000
      );
    }
  },
  { deep: true }
);
</script>
